import { createLiveVoiceEventHandler } from "../live-voice-events.js";
import { withSpaceHeaders } from "../rpc.js";

export class LiveVoiceBudgetError extends Error {}
export class LiveVoiceServiceError extends Error {}
export interface LiveCall {
  mute(muted: boolean): void;
  close(): Promise<void>;
}

/** Browser PCM transport. Only a one-use app ticket enters the browser. */
export async function connectRealtimeCall(input: {
  botId: string;
  signal: AbortSignal;
  phase(phase: "listening" | "thinking" | "speaking" | "reconnecting"): void;
  caption(text: string): void;
  error(error?: Error): void;
  approval?(): void;
}): Promise<LiveCall> {
  const headers = withSpaceHeaders({ "content-type": "application/json" });
  const post = async (action: string, body: unknown, signal?: AbortSignal) => {
    const response = await fetch(`/api/voice/live/${action}`, {
      method: "POST",
      headers,
      credentials: "same-origin",
      body: JSON.stringify(body),
      signal,
    });
    if (response.status === 402) throw new LiveVoiceBudgetError();
    if (!response.ok) throw new Error("Wafi couldn't connect. Try again.");
    return response.json();
  };
  let stream: MediaStream | undefined;
  let sessionId: string | undefined;
  let socket: WebSocket | undefined;
  let capture: AudioWorkletNode | undefined;
  let source: MediaStreamAudioSourceNode | undefined;
  let silent: GainNode | undefined;
  let closed = false;
  let muted = false;
  let ending: Promise<void> | undefined;
  let clearConnection: (() => void) | undefined;
  let rejectConnection: ((error: Error) => void) | undefined;
  let readyOnce = false;
  let reconnecting = false;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let cancelRetry: (() => void) | undefined;
  let lostNetwork = false;
  let micNeeded = false;
  let acquiringMic = false;
  let lastMicAttempt = -Infinity;
  let micTimer: ReturnType<typeof setTimeout> | undefined;
  const endSession = () => {
    const endedSession = sessionId;
    sessionId = undefined;
    return endedSession
      ? post("end", { sessionId: endedSession }).catch(() => undefined)
      : Promise.resolve();
  };
  const audioConstraints = {
    audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
  };
  let context: AudioContext;
  try {
    context = new AudioContext({ sampleRate: 24000 });
  } catch {
    context = new AudioContext();
  }
  const playback = context.createGain();
  playback.connect(context.destination);
  // Resume while the initiating tap still carries user activation.
  const resumed = context.resume();
  void resumed.catch(() => undefined);
  let nextAudioAt = 0;
  const playing = new Set<AudioBufferSourceNode>();
  const pending = new Set<Promise<unknown>>();
  let interrupted = false;
  let responseFinished = false;
  let audioItem:
    | { id: string; contentIndex: number; startedAt: number; duration: number }
    | undefined;
  const stopAudio = () => {
    playback.gain.cancelScheduledValues(context.currentTime);
    playback.gain.setValueAtTime(playback.gain.value, context.currentTime);
    playback.gain.linearRampToValueAtTime(0, context.currentTime + 0.015);
    for (const node of playing) {
      node.onended = null;
      node.stop(context.currentTime + 0.015);
      node.disconnect();
    }
    playing.clear();
    nextAudioAt = 0;
  };
  const close = () => {
    if (ending) return ending;
    closed = true;
    clearTimeout(retryTimer);
    clearTimeout(micTimer);
    cancelRetry?.();
    window.removeEventListener("online", online);
    window.removeEventListener("offline", offline);
    document.removeEventListener("visibilitychange", visible);
    context.removeEventListener("statechange", audioState);
    clearConnection?.();
    rejectConnection?.(new Error("Call ended"));
    input.signal.removeEventListener("abort", aborted);
    stream?.getTracks().forEach((track) => {
      track.stop();
    });
    capture?.disconnect();
    if (capture) capture.port.onmessage = null;
    source?.disconnect();
    silent?.disconnect();
    socket?.close();
    stopAudio();
    const audioClosed = context.close().catch(() => undefined);
    ending = (async () => {
      await audioClosed;
      await Promise.allSettled([...pending]);
      await endSession();
    })();
    return ending;
  };
  const aborted = () => {
    void close();
  };
  const fail = (error?: Error) => {
    if (closed) return;
    void close();
    input.error(error);
  };
  const send = (event: unknown) => {
    if (socket?.readyState === WebSocket.OPEN && !closed) {
      if (socket.bufferedAmount > 256 * 1024) return fail();
      socket.send(JSON.stringify(event));
    }
  };
  let connectSocket: () => Promise<void>;
  const reconnect = async () => {
    if (closed || !readyOnce || reconnecting) return;
    reconnecting = true;
    input.phase("reconnecting");
    stopAudio();
    responseFinished = false;
    interrupted = false;
    audioItem = undefined;
    if (socket) socket.onclose = null;
    socket?.close();
    void endSession();
    for (const delay of [500, 1500, 3000]) {
      await new Promise<void>((resolve) => {
        cancelRetry = () => {
          clearTimeout(retryTimer);
          resolve();
        };
        retryTimer = setTimeout(resolve, delay);
      });
      cancelRetry = undefined;
      if (closed) return;
      try {
        await connectSocket();
        if (closed) return;
        reconnecting = false;
        lostNetwork = false;
        input.phase("listening");
        return;
      } catch (error) {
        if (closed) return;
        if (error instanceof LiveVoiceBudgetError || error instanceof LiveVoiceServiceError) {
          fail(error);
          return;
        }
        if (socket) socket.onclose = null;
        socket?.close();
        void endSession();
      }
    }
    fail();
  };
  const offline = () => {
    lostNetwork = true;
  };
  const online = () => {
    if (!lostNetwork) return;
    lostNetwork = false;
    if (reconnecting) cancelRetry?.();
    else void reconnect();
  };
  const resumeAudio = () => {
    if (!closed) void context.resume().catch(() => undefined);
  };
  const recoverMic = () => {
    if (
      closed ||
      !micNeeded ||
      acquiringMic ||
      document.visibilityState !== "visible" ||
      String(context.state) === "interrupted" ||
      context.state === "suspended"
    )
      return;
    clearTimeout(micTimer);
    const remaining = lastMicAttempt + 10_000 - Date.now();
    if (remaining > 0) {
      micTimer = setTimeout(recoverMic, remaining);
      return;
    }
    acquiringMic = true;
    lastMicAttempt = Date.now();
    void navigator.mediaDevices
      .getUserMedia(audioConstraints)
      .then((replacement) => {
        if (closed) {
          replacement.getTracks().forEach((track) => {
            track.stop();
          });
          return;
        }
        source?.disconnect();
        stream?.getTracks().forEach((track) => {
          track.stop();
        });
        stream = replacement;
        stream.getAudioTracks().forEach((track) => {
          track.enabled = !muted;
        });
        source = context.createMediaStreamSource(stream);
        source.connect(capture!);
        micNeeded = false;
        watchMic();
        resumeAudio();
      })
      .catch(() => {
        if (
          document.visibilityState === "visible" &&
          String(context.state) !== "interrupted" &&
          context.state !== "suspended"
        )
          fail();
      })
      .finally(() => {
        acquiringMic = false;
      });
  };
  const visible = () => {
    if (document.visibilityState === "visible") {
      resumeAudio();
      recoverMic();
    }
  };
  const audioState = () => {
    if (context.state === "suspended" || String(context.state) === "interrupted") resumeAudio();
    recoverMic();
  };
  const watchMic = () => {
    stream?.getAudioTracks().forEach((track) => {
      track.addEventListener(
        "ended",
        () => {
          micNeeded = true;
          recoverMic();
        },
        { once: true },
      );
    });
  };
  input.signal.addEventListener("abort", aborted, { once: true });
  try {
    input.signal.throwIfAborted();
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
      },
    });
    if (closed || input.signal.aborted) {
      stream.getTracks().forEach((track) => {
        track.stop();
      });
      throw new Error("Call ended");
    }
    await resumed;
    await context.audioWorklet.addModule("/audio/call-capture.js");
    if (closed) throw new Error("Call ended");
    const openConnection = async () => {
      const session = (await post("session", { botId: input.botId }, input.signal)) as {
        value: string;
        session: { id: string };
      };
      const connectionSessionId = session.session.id;
      if (closed) {
        await post("end", { sessionId: connectionSessionId });
        throw new Error("Call ended");
      }
      sessionId = connectionSessionId;
      const url = new URL("/api/voice/live/socket", window.location.href);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      url.searchParams.set("session", sessionId);
      const space = headers.get("x-rakazo-space-id");
      if (space) url.searchParams.set("space", space);
      const current = new WebSocket(url, [session.value]);
      socket = current;
      const handle = createLiveVoiceEventHandler({
        tool: async (call) =>
          (await post("tool", { sessionId: connectionSessionId, ...call }, input.signal)).result,
        // The server records prepaid duration receipts; no client usage estimate is trusted.
        usage: async () => {},
        send: (event) => {
          if (socket === current && !closed && !reconnecting) send(event);
        },
        caption: (text) => {
          if (socket === current && !closed && !reconnecting) input.caption(text);
        },
        approval: () => {
          if (socket === current && !closed && !reconnecting) input.approval?.();
        },
        phase: (phase) => {
          if (socket !== current || closed || reconnecting) return;
          if (phase === "listening" && playing.size > 0) return;
          input.phase(phase);
        },
      });
      await new Promise<void>((resolve, reject) => {
        rejectConnection = reject;
        const timeout = setTimeout(() => {
          reject(new Error("Wafi couldn't connect. Try again."));
        }, 20_000);
        clearConnection = () => {
          clearTimeout(timeout);
        };
        let connected = false;
        const ready = () => {
          connected = true;
          clearTimeout(timeout);
          rejectConnection = undefined;
          resolve();
        };
        const failed = () => {
          clearTimeout(timeout);
          reject(new Error("Wafi couldn't connect. Try again."));
          if (connected && !closed && socket === current) void reconnect();
        };
        current.onerror = () => {
          if (!connected) failed();
        };
        current.onclose = (event) => {
          if (socket !== current || closed) return;
          if (event.code === 4002 && !closed) {
            const error = new LiveVoiceBudgetError();
            clearTimeout(timeout);
            reject(error);
            if (connected) fail(error);
          } else failed();
        };
        current.onmessage = (message) => {
          if (closed || socket !== current) return;
          const work = Promise.resolve()
            .then(async () => {
              if (closed || socket !== current || (connected && reconnecting)) return;
              const event = JSON.parse(message.data) as Record<string, unknown>;
              if (event.type === "session.updated") return ready();
              if (event.type === "error" && event.reason === "service_unavailable") {
                const error = new LiveVoiceServiceError();
                clearTimeout(timeout);
                reject(error);
                if (connected) fail(error);
                return;
              }
              if (event.type === "response.created") {
                interrupted = false;
                responseFinished = false;
                audioItem = undefined;
              }
              if (event.type === "input_audio_buffer.speech_started") {
                interrupted = true;
                if (audioItem && playing.size > 0)
                  send({
                    type: "conversation.item.truncate",
                    item_id: audioItem.id,
                    content_index: audioItem.contentIndex,
                    audio_end_ms: Math.max(
                      0,
                      Math.floor(
                        Math.min(audioItem.duration, context.currentTime - audioItem.startedAt) *
                          1000,
                      ),
                    ),
                  });
                stopAudio();
              }
              if (
                interrupted &&
                (event.type === "response.output_audio.delta" ||
                  event.type === "response.output_audio_transcript.delta")
              )
                return;
              if (event.type === "response.done") responseFinished = true;
              if (event.type === "response.output_audio.delta" && typeof event.delta === "string") {
                const bytes = Uint8Array.from(atob(event.delta), (char) => char.charCodeAt(0));
                if (bytes.length % 2 !== 0 || nextAudioAt - context.currentTime > 30)
                  throw new Error();
                const buffer = context.createBuffer(1, bytes.length / 2, 24000);
                const samples = buffer.getChannelData(0);
                const view = new DataView(bytes.buffer);
                for (let i = 0; i < samples.length; i++)
                  samples[i] = view.getInt16(i * 2, true) / 32768;
                const node = context.createBufferSource();
                node.buffer = buffer;
                if (playing.size === 0) {
                  playback.gain.cancelScheduledValues(context.currentTime);
                  playback.gain.setValueAtTime(1, context.currentTime);
                }
                node.connect(playback);
                playing.add(node);
                node.onended = () => {
                  playing.delete(node);
                  node.disconnect();
                  if (!closed && responseFinished && playing.size === 0) input.phase("listening");
                };
                const start = Math.max(context.currentTime, nextAudioAt);
                if (typeof event.item_id === "string" && audioItem?.id !== event.item_id) {
                  audioItem = {
                    id: event.item_id,
                    contentIndex: typeof event.content_index === "number" ? event.content_index : 0,
                    startedAt: start,
                    duration: 0,
                  };
                }
                if (audioItem) audioItem.duration += buffer.duration;
                node.start(start);
                nextAudioAt = start + buffer.duration;
                input.phase("speaking");
              }
              await handle(event);
            })
            .catch(() => {
              failed();
            });
          pending.add(work);
          void work.finally(() => pending.delete(work));
        };
      });
    };
    // Each new socket has its own handshake and event handler.
    connectSocket = openConnection;
    await openConnection();
    readyOnce = true;
    if (closed) throw new Error("Call ended");
    source = context.createMediaStreamSource(stream);
    capture = new AudioWorkletNode(context, "call-capture");
    silent = context.createGain();
    silent.gain.value = 0;
    source.connect(capture).connect(silent).connect(context.destination);
    capture.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
      if (muted || closed) return;
      const bytes = new Uint8Array(event.data);
      let binary = "";
      for (const byte of bytes) binary += String.fromCharCode(byte);
      send({ type: "input_audio_buffer.append", audio: btoa(binary) });
    };
    watchMic();
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    document.addEventListener("visibilitychange", visible);
    context.addEventListener("statechange", audioState);
    input.phase("listening");
    return {
      mute: (value) => {
        muted = value;
        stream?.getAudioTracks().forEach((track) => {
          track.enabled = !value;
        });
      },
      close,
    };
  } catch (error) {
    await close();
    throw error;
  }
}
